import { pluralRu } from './readyWorkStatus.js';

/**
 * Отказ в платном действии (Billing, SpendDenialResponder): 402 — не хватает токенов или
 * нужна подписка, 409 — подписка заморожена. Здесь только чистые функции (под node-тестами),
 * состояние модалки — в composables/billing/usePaywall.js.
 */

export const PAYWALL_CODE = {
  INSUFFICIENT: 'insufficient_coins',
  SUBSCRIPTION_REQUIRED: 'subscription_required',
  FROZEN: 'subscription_frozen',
};

const KNOWN_CODES = new Set(Object.values(PAYWALL_CODE));

export const FEATURE_LABELS = {
  document: 'полная генерация работы',
  task: 'решение задачи',
  ready_work: 'готовая работа',
  drawing: 'чертёж',
  // Легаси-конструктор: своя фича биллинга, но для пользователя это тот же чертёж
  drawing_constructor: 'чертёж',
  presentation: 'презентация',
};

export function coinsWord(count) {
  return pluralRu(count, 'токен', 'токена', 'токенов');
}

function toInt(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? Math.trunc(number) : fallback;
}

/**
 * Разбор ошибки http-клиента (ApiError: status + data). Возвращает null, если это не отказ
 * биллинга — тогда вызывающий код обрабатывает ошибку как раньше.
 *
 * @returns {null | {
 *   status: number, code: string, message: string, feature: string | null,
 *   price: number, balance: number, deficit: number, frozen: boolean, modalType: 'plans' | 'extras',
 * }}
 */
export function parseSpendDenial(error) {
  const status = Number(error?.status ?? error?.response?.status);
  const data = error?.data ?? error?.response?.data ?? null;

  if (!data || typeof data !== 'object') return null;
  if (status !== 402 && status !== 409) return null;

  const code = typeof data.code === 'string' ? data.code : null;
  const billing = data.billing && typeof data.billing === 'object' ? data.billing : null;

  if (!KNOWN_CODES.has(code) && !billing) return null;

  const resolvedCode =
    code ??
    (status === 409
      ? PAYWALL_CODE.FROZEN
      : billing?.status === 'subscription_required'
        ? PAYWALL_CODE.SUBSCRIPTION_REQUIRED
        : PAYWALL_CODE.INSUFFICIENT);

  const frozen = status === 409 || resolvedCode === PAYWALL_CODE.FROZEN;
  const modalType =
    data.modal_type === 'plans' || data.modal_type === 'extras'
      ? data.modal_type
      : resolvedCode === PAYWALL_CODE.SUBSCRIPTION_REQUIRED
        ? 'plans'
        : 'extras';

  return {
    status,
    code: resolvedCode,
    message: typeof data.message === 'string' ? data.message : '',
    feature: typeof billing?.feature === 'string' ? billing.feature : null,
    price: toInt(billing?.price),
    balance: toInt(billing?.balance),
    deficit: toInt(billing?.deficit, Math.max(0, toInt(billing?.price) - toInt(billing?.balance))),
    frozen,
    modalType,
  };
}

/** Текст paywall: «Не хватает 70 токенов: полная генерация работы стоит 100, на балансе 30». */
export function describeDenial(denial) {
  if (!denial) return '';

  const featureLabel = denial.feature ? FEATURE_LABELS[denial.feature] || denial.feature : 'действие';

  if (denial.frozen) {
    return 'Подписка заморожена: списания на паузе. Разморозьте её, чтобы продолжить.';
  }

  if (denial.code === PAYWALL_CODE.SUBSCRIPTION_REQUIRED) {
    return `${capitalize(featureLabel)} входит в подписку. Оформите тариф, чтобы продолжить.`;
  }

  const deficit = denial.deficit > 0 ? denial.deficit : Math.max(0, denial.price - denial.balance);

  return `Не хватает ${deficit} ${coinsWord(deficit)}: ${featureLabel} стоит ${denial.price} ${coinsWord(denial.price)}, на балансе ${denial.balance}.`;
}

/** Короткая подпись для кнопки/бейджа: «нужно ещё 70 токенов». */
export function describeDeficitShort(denial) {
  if (!denial || denial.frozen) return '';
  if (denial.code === PAYWALL_CODE.SUBSCRIPTION_REQUIRED) return 'нужна подписка';

  const deficit = denial.deficit > 0 ? denial.deficit : Math.max(0, denial.price - denial.balance);

  return deficit > 0 ? `нужно ещё ${deficit} ${coinsWord(deficit)}` : '';
}

/**
 * «250 токенов ≈ 2 работы или 25 задач» — подсказка к балансу по ценам каталога.
 *
 * `prices.document` — ОПОРНАЯ цена работы (типовая ступень лесенки цен по объёму), её отдаёт
 * WalletPresenter. Объём будущей работы в момент показа баланса неизвестен, поэтому эквивалент
 * верен для типовой работы: короткая обойдётся дешевле, большая — дороже.
 */
export function describeBalanceEquivalent(balance, prices = {}) {
  const coins = toInt(balance);
  const documentPrice = toInt(prices?.document);
  const taskPrice = toInt(prices?.task);
  const parts = [];

  if (documentPrice > 0) {
    const works = Math.floor(coins / documentPrice);
    parts.push(`${works} ${pluralRu(works, 'работа', 'работы', 'работ')}`);
  }
  if (taskPrice > 0) {
    const tasks = Math.floor(coins / taskPrice);
    parts.push(`${tasks} ${pluralRu(tasks, 'задача', 'задачи', 'задач')}`);
  }

  return parts.length ? `≈ ${parts.join(' или ')}` : '';
}

/**
 * «≈ 3 чертежа» на эти токены по цене каталога — строка состава тарифа в модалке оплаты (релиз
 * 2026-10-03-drawings-cabinet-tariffs). '' — чертёж не за токены (0 — входит в подписку) или не хватает ни на один.
 */
export function describeDrawingsEquivalent(balance, prices = {}) {
  const drawingPrice = toInt(prices?.drawing);
  if (!(drawingPrice > 0)) return '';

  const drawings = Math.floor(toInt(balance) / drawingPrice);

  return drawings > 0 ? `≈ ${drawings} ${pluralRu(drawings, 'чертёж', 'чертежа', 'чертежей')}` : '';
}

/** Подписи фич без счётчика в порядке витрины. Цена 0 = «входит в подписку» (решение 0.2), не «бесплатно». */
const INCLUDED_LABELS = [
  ['document', 'работы'],
  ['task', 'задачи'],
  ['ready_work', 'готовые работы'],
  ['drawing', 'чертежи'],
  ['presentation', 'презентации'],
];

/**
 * «чертежи», «работы и чертежи», «работы, задачи и чертежи» — что входит в подписку без счётчика
 * по ценам каталога ({ document: 0, task: 10, … }). Не заданная цена (null — каталог не засеян) —
 * не ноль. Пусто — всё со счётчиком.
 */
export function describeIncludedFeatures(prices = {}) {
  const labels = INCLUDED_LABELS.filter(([code]) => isZeroPrice(prices?.[code])).map(([, label]) => label);

  if (labels.length <= 1) return labels.join('');

  return `${labels.slice(0, -1).join(', ')} и ${labels[labels.length - 1]}`;
}

/** «Работы и чертежи без ограничений» — строка состава тарифа; '' — всё со счётчиком. */
export function describeIncludedLine(prices = {}) {
  const included = describeIncludedFeatures(prices);

  return included ? `${capitalize(included)} без ограничений` : '';
}

function isZeroPrice(value) {
  // Number(null) === 0 — поэтому сначала проверяем, что цена задана
  return value !== null && value !== undefined && value !== '' && Number(value) === 0;
}

function capitalize(text) {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
}
